import React, { useEffect, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { ArrowRight, ArrowLeft, ChevronRight, Terminal, Cpu, Layers } from "lucide-react";
import features, { featureList } from "./featureData";

/* ─── Animated section wrapper ─────────────────────────────────────── */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Grid line decoration ─────────────────────────────────────────── */
function GridLines() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Vertical lines */}
      {[20, 40, 60, 80].map(p => (
        <div key={`v${p}`} className="absolute top-0 bottom-0" style={{ left: `${p}%`, width: "1px", background: "linear-gradient(180deg, transparent 0%, rgba(75,123,245,0.06) 30%, rgba(75,123,245,0.06) 70%, transparent 100%)" }} />
      ))}
      {/* Horizontal lines */}
      {[25, 50, 75].map(p => (
        <div key={`h${p}`} className="absolute left-0 right-0" style={{ top: `${p}%`, height: "1px", background: "linear-gradient(90deg, transparent 0%, rgba(75,123,245,0.06) 20%, rgba(75,123,245,0.06) 80%, transparent 100%)" }} />
      ))}
    </div>
  );
}

/* ─── Floating code annotation ─────────────────────────────────────── */
function CodeAnnotation({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.8 + delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute bg-[#0A1230]/95 backdrop-blur-sm border border-[#1E2D55] px-3 py-2 shadow-xl shadow-black/20 ${className}`}
    >
      <div className="flex items-center gap-2">
        <Terminal className="w-3 h-3 text-[#4B7BF5]" strokeWidth={2} />
        <span className="font-['JetBrains_Mono'] text-[10px] text-[#4B7BF5]/80">{children}</span>
      </div>
    </motion.div>
  );
}

/* ─── Screenshot showcase — the centerpiece ────────────────────────── */
function ScreenshotShowcase({ screenshot, label, color }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [8, 0, 0, -4]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.92, 1, 1, 0.96]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.6, 0]);

  return (
    <div ref={containerRef} className="relative py-8" style={{ perspective: "1200px" }}>
      {/* Ambient glow behind screenshot */}
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute inset-x-[10%] top-[15%] bottom-[15%] rounded-full blur-[80px]"
        aria-hidden="true"
      >
        <div className="w-full h-full" style={{ background: `radial-gradient(ellipse, ${color}30, transparent 70%)` }} />
      </motion.div>

      <motion.div
        style={{ rotateX, scale, transformOrigin: "center center" }}
        className="relative"
      >
        {/* Browser chrome frame */}
        <div className="bg-[#0A1230] border border-[#1E2D55] shadow-2xl shadow-black/40 overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1E2D55] bg-[#070E24]">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-2 bg-[#0F1B3D] border border-[#1E2D55] px-4 py-1 max-w-xs w-full">
                <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-[#28C840] flex-shrink-0" fill="currentColor"><circle cx="6" cy="6" r="5"/></svg>
                <span className="font-['JetBrains_Mono'] text-[10px] text-[#EEEADF]/30 truncate">acadence.in/dashboard/{label.toLowerCase()}</span>
              </div>
            </div>
            <div className="w-16" />
          </div>

          {/* Screenshot image */}
          <div className="relative">
            <img
              src={screenshot}
              alt={`${label} module — Acadence ERP`}
              className="w-full block"
              loading="lazy"
            />

            {/* Scan line effect */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(75,123,245,0.015) 2px, rgba(75,123,245,0.015) 4px)",
              }}
            />

            {/* Animated scan beam */}
            <motion.div
              className="absolute left-0 right-0 h-[1px] pointer-events-none"
              style={{ background: `linear-gradient(90deg, transparent, ${color}40, transparent)` }}
              animate={{ top: ["0%", "100%"] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />
          </div>
        </div>

        {/* Floating annotations */}
        <CodeAnnotation className="hidden lg:flex -left-4 top-[20%] z-10" delay={0}>
          api.status: 200 OK
        </CodeAnnotation>
        <CodeAnnotation className="hidden lg:flex -right-4 top-[40%] z-10" delay={0.15}>
          render: 16ms
        </CodeAnnotation>
        <CodeAnnotation className="hidden lg:flex -left-4 bottom-[25%] z-10" delay={0.3}>
          queries: optimized
        </CodeAnnotation>

        {/* Corner accents */}
        <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 pointer-events-none" style={{ borderColor: color }} />
        <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 pointer-events-none" style={{ borderColor: color }} />
        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 pointer-events-none" style={{ borderColor: color }} />
        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 pointer-events-none" style={{ borderColor: color }} />
      </motion.div>
    </div>
  );
}

/* ─── Capability card ──────────────────────────────────────────────── */
function CapabilityCard({ title, desc, index, color }) {
  return (
    <Reveal delay={index * 0.08} className="group">
      <div className="relative bg-white border border-[#E2DDD4] p-7 hover:border-[#0F1B3D] transition-all duration-300 h-full">
        {/* Index number */}
        <div className="flex items-center justify-between mb-5">
          <span className="font-['JetBrains_Mono'] text-[11px] font-bold tracking-wider group-hover:text-[#4B7BF5] transition-colors" style={{ color: `${color}60` }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <div className="w-6 h-[2px] group-hover:w-10 transition-all duration-300" style={{ background: color }} />
        </div>
        <h3 className="font-['Syne'] font-bold text-[15px] text-[#0F1B3D] mb-3 leading-snug">{title}</h3>
        <p className="font-['DM_Sans'] text-sm text-[#7A7468] leading-relaxed">{desc}</p>
      </div>
    </Reveal>
  );
}

/* ─── Stats bar ────────────────────────────────────────────────────── */
function StatsBar({ stats, color }) {
  return (
    <div className="grid grid-cols-3 gap-px bg-[#1E2D55]">
      {stats.map((s, i) => (
        <Reveal key={i} delay={i * 0.1}>
          <div className="bg-[#0F1B3D] p-6 text-center">
            <p className="font-['JetBrains_Mono'] font-bold text-2xl md:text-3xl text-[#EEEADF] mb-1">{s.value}</p>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#EEEADF]/30">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ─── Feature navigation ───────────────────────────────────────────── */
function FeatureNav({ current }) {
  const currentIdx = featureList.findIndex(f => f.slug === current);
  const prev = featureList[(currentIdx - 1 + featureList.length) % featureList.length];
  const next = featureList[(currentIdx + 1) % featureList.length];

  return (
    <div className="grid grid-cols-2 border-t border-[#D8D4CB]">
      <Link
        to={`/features/${prev.slug}`}
        className="group flex items-center gap-4 p-8 md:p-12 hover:bg-white transition-colors border-r border-[#D8D4CB]"
      >
        <ArrowLeft className="w-5 h-5 text-[#BDBAA8] group-hover:text-[#0F1B3D] transition-colors flex-shrink-0" />
        <div>
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Previous</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0F1B3D]">{prev.label}</p>
        </div>
      </Link>
      <Link
        to={`/features/${next.slug}`}
        className="group flex items-center justify-end gap-4 p-8 md:p-12 hover:bg-white transition-colors text-right"
      >
        <div>
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Next</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0F1B3D]">{next.label}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-[#BDBAA8] group-hover:text-[#0F1B3D] transition-colors flex-shrink-0" />
      </Link>
    </div>
  );
}

/* ─── All features grid (for /features index) ──────────────────────── */
export function FeaturesIndex() {
  useEffect(() => {
    document.title = "Features — Acadence Education ERP";
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0F1B3D]/95 backdrop-blur-md border-b border-[#1E2D55]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
              <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
              <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
              <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
            </svg>
            <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/60 hover:text-[#EEEADF] transition-colors px-4 py-2">Sign in</Link>
            <Link to="/contact" className="font-['Syne'] text-xs font-bold uppercase tracking-widest bg-[#4B7BF5] text-white px-5 py-2.5 hover:bg-white hover:text-[#0F1B3D] transition-colors">Get a Demo</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-[#0F1B3D] noise-bg pt-32 pb-20 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#4B7BF5]/6 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-6">
              <Layers className="w-4 h-4 text-[#4B7BF5]" />
              <span className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5]">Platform Capabilities</span>
            </div>
            <h1 className="font-['Instrument_Serif'] text-[#EEEADF] leading-[0.95] mb-6" style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              Every module.<br />
              <em className="italic text-[#4B7BF5]">Purpose-built.</em>
            </h1>
            <p className="font-['DM_Sans'] text-[#EEEADF]/45 text-lg max-w-lg leading-relaxed">
              Explore each module in depth — see exactly how Acadence handles every aspect of institute management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D8D4CB]">
          {featureList.map((f, i) => (
            <Reveal key={f.slug} delay={i * 0.05}>
              <Link
                to={`/features/${f.slug}`}
                className="group bg-[#F5F2EA] hover:bg-white p-8 transition-all duration-300 block h-full"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${f.color}15` }}>
                    <f.icon className="w-5 h-5" style={{ color: f.color }} strokeWidth={1.5} />
                  </div>
                  <span className="font-['JetBrains_Mono'] text-[11px] text-[#D8D4CB] group-hover:text-[#4B7BF5] transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-['Syne'] font-bold text-lg text-[#0F1B3D] mb-2">{f.label}</h3>
                <p className="font-['DM_Sans'] text-sm text-[#7A7468] leading-relaxed mb-5 line-clamp-2">{f.description}</p>
                <div className="flex items-center gap-2 text-[#BDBAA8] group-hover:text-[#0F1B3D] transition-colors">
                  <span className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest">Explore</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#4B7BF5] py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-['Instrument_Serif'] text-white text-3xl md:text-4xl leading-tight">Ready to see it live?</h2>
          <Link to="/contact" className="bg-[#0F1B3D] text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-10 py-4 hover:bg-[#162044] transition-colors flex items-center gap-2 flex-shrink-0">
            Request a Demo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B3D] border-t border-[#1E2D55] py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/><rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/><rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/></svg>
            <span className="font-['DM_Sans'] font-medium text-sm text-[#EEEADF] tracking-[0.08em]">acadence</span>
          </div>
          <p className="font-['DM_Sans'] text-[#EEEADF]/25 text-xs">© 2026 Acadence. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ─── MAIN FEATURE PAGE ────────────────────────────────────────────── */
export default function FeaturePage() {
  const { slug } = useParams();
  const feature = features[slug];

  useEffect(() => {
    if (feature) {
      document.title = `${feature.label} — Acadence Education ERP`;
    }
    window.scrollTo(0, 0);
  }, [slug, feature]);

  if (!feature) return <Navigate to="/features" replace />;

  const Icon = feature.icon;
  const headlineLines = feature.headline.split("\n");

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0F1B3D]/95 backdrop-blur-md border-b border-[#1E2D55]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5">
              <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8">
                <rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/>
                <rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/>
                <rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/>
              </svg>
              <span className="font-['DM_Sans'] font-medium text-lg text-[#EEEADF] tracking-[0.08em]">acadence</span>
            </Link>
            <div className="hidden md:flex items-center gap-1.5 text-[#EEEADF]/25">
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/features" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/40 hover:text-[#EEEADF] transition-colors">Features</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#4B7BF5]">{feature.label}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="font-['Syne'] text-xs font-bold uppercase tracking-widest text-[#EEEADF]/60 hover:text-[#EEEADF] transition-colors px-4 py-2 hidden sm:block">Sign in</Link>
            <Link to="/contact" className="font-['Syne'] text-xs font-bold uppercase tracking-widest bg-[#4B7BF5] text-white px-5 py-2.5 hover:bg-white hover:text-[#0F1B3D] transition-colors">Get a Demo</Link>
          </div>
        </div>
      </nav>

      {/* Hero section */}
      <section className="relative bg-[#0F1B3D] noise-bg pt-32 pb-24 overflow-hidden">
        <GridLines />
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full blur-[120px] pointer-events-none" style={{ background: `radial-gradient(ellipse, ${feature.color}12, transparent 70%)` }} />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Text */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-center gap-3 mb-8"
              >
                <div className="w-8 h-8 flex items-center justify-center" style={{ background: `${feature.color}20` }}>
                  <Icon className="w-4 h-4" style={{ color: feature.color }} strokeWidth={1.5} />
                </div>
                <span className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#4B7BF5]">{feature.tagline}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="font-['Instrument_Serif'] text-[#EEEADF] leading-[0.95] mb-8"
                style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)" }}
              >
                {headlineLines[0]}<br />
                <em className="italic" style={{ color: feature.color }}>{headlineLines[1]}</em>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="font-['DM_Sans'] text-[#EEEADF]/50 text-lg leading-relaxed max-w-lg mb-10"
              >
                {feature.description}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.35 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/contact" className="inline-flex items-center gap-2 text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-8 py-4 hover:brightness-110 transition-all active:scale-95" style={{ background: feature.color }}>
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" className="inline-flex items-center gap-2 border border-[#EEEADF]/15 text-[#EEEADF]/60 font-['Syne'] font-bold text-xs uppercase tracking-widest px-8 py-4 hover:border-[#EEEADF]/40 hover:text-[#EEEADF] transition-colors">
                  Sign In
                </Link>
              </motion.div>
            </div>

            {/* Right — Tech badge cluster */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="hidden lg:flex flex-col items-center gap-6"
            >
              {/* Large icon */}
              <div className="relative">
                <div className="w-40 h-40 flex items-center justify-center border border-[#1E2D55] bg-[#0A1230]/50" style={{ boxShadow: `0 0 80px ${feature.color}15` }}>
                  <Icon className="w-16 h-16" style={{ color: feature.color }} strokeWidth={0.8} />
                </div>
                {/* Orbiting indicators */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-[-20px]"
                >
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full" style={{ background: feature.color }} />
                </motion.div>
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-[-40px]"
                >
                  <div className="absolute bottom-0 right-0 w-1.5 h-1.5 rounded-full bg-[#4B7BF5]/50" />
                </motion.div>
              </div>

              {/* Tech indicators */}
              <div className="flex gap-3">
                {[
                  { icon: Terminal, label: "REST API" },
                  { icon: Cpu, label: "Real-time" },
                  { icon: Layers, label: "Modular" },
                ].map((t, i) => (
                  <motion.div
                    key={t.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.1 }}
                    className="flex items-center gap-2 bg-[#0A1230] border border-[#1E2D55] px-3 py-2"
                  >
                    <t.icon className="w-3 h-3 text-[#4B7BF5]" strokeWidth={1.5} />
                    <span className="font-['JetBrains_Mono'] text-[9px] text-[#EEEADF]/40">{t.label}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <StatsBar stats={feature.stats} color={feature.color} />

      {/* Screenshot showcase */}
      <section className="bg-[#F5F2EA] py-16 md:py-24 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#7A7468] mb-3">— Live Interface</p>
              <h2 className="font-['Syne'] font-bold text-2xl md:text-3xl text-[#0F1B3D]">See it in action</h2>
            </div>
          </Reveal>
          <ScreenshotShowcase screenshot={feature.screenshot} label={feature.label} color={feature.color} />
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-white py-16 md:py-24 border-y border-[#D8D4CB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <Reveal>
            <div className="mb-14">
              <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#7A7468] mb-3">— Capabilities</p>
              <h2 className="font-['Syne'] font-bold text-2xl md:text-3xl text-[#0F1B3D]">What's inside</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2DDD4]">
            {feature.capabilities.map((cap, i) => (
              <CapabilityCard key={i} title={cap.title} desc={cap.desc} index={i} color={feature.color} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" style={{ background: feature.color }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-['Instrument_Serif'] text-white text-3xl md:text-5xl leading-tight mb-8 max-w-2xl mx-auto">
              Ready to transform your {feature.label.toLowerCase()}?
            </h2>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="bg-[#0F1B3D] text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-10 py-4 hover:bg-[#162044] transition-colors flex items-center gap-2">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/features" className="border border-white/30 text-white font-['Syne'] font-bold text-xs uppercase tracking-widest px-10 py-4 hover:bg-white/10 transition-colors">
                All Features
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Navigate between features */}
      <FeatureNav current={slug} />

      {/* Footer */}
      <footer className="bg-[#0F1B3D] border-t border-[#1E2D55] py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <svg viewBox="0 0 32 32" fill="none" className="w-6 h-6"><rect x="1" y="21" width="8" height="8" rx="2" fill="#2A3A66"/><rect x="12" y="13" width="8" height="8" rx="2" fill="#4B7BF5" opacity="0.5"/><rect x="22" y="5" width="8" height="8" rx="2" fill="#4B7BF5"/></svg>
            <span className="font-['DM_Sans'] font-medium text-sm text-[#EEEADF] tracking-[0.08em]">acadence</span>
          </div>
          <p className="font-['DM_Sans'] text-[#EEEADF]/25 text-xs">© 2026 Acadence. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/contact" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/30 hover:text-[#EEEADF] transition-colors">Contact</Link>
            <a href="mailto:info@acadence.in" className="font-['Syne'] text-[10px] font-bold uppercase tracking-widest text-[#EEEADF]/30 hover:text-[#EEEADF] transition-colors">info@acadence.in</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
